import { ResizeHandle } from '@repo/studio-components-resize-handle'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { tw } from 'utils/classnames'
import type { useDockResizeHandle } from '../../hooks/use-dock-resize-handle'
import type { useFloatingPanelPosition } from '../../hooks/use-floating-panel-position'
import { useAidaPanelImageDrop } from '../../hooks/use-aida-panel-image-drop'
import { useAidaFileDropStore } from '../../stores/file-drop-store'
import { useAidaPipStore } from '../../stores/picture-in-picture-store'
import { closeAidaMediaLightbox, useAidaMediaLightboxStore } from '../lightbox/store'
import { AidaAwayChrome } from '../shell/aida-panel-chrome'
import { AidaImageDropOverlay } from '../shell/aida-image-drop-chrome'
import { AidaPipAwayNotice } from '../shell/aida-picture-in-picture'

function DockResizeHandle({ resize }: { resize: ReturnType<typeof useDockResizeHandle> }) {
  const { t } = useTranslation('components')
  return (
    <ResizeHandle
      variant="edge"
      currentWidth={resize.dockWidth}
      minWidth={resize.bounds.min}
      maxWidth={resize.bounds.max}
      isDragging={resize.isDragging}
      onPointerDown={resize.handleProps.onPointerDown}
      onReset={resize.onReset}
      onKeyDown={resize.onKeyDown}
      ariaLabel={t('studio.components.aida.resize_panel')}
    />
  )
}

export function AidaDetachedPanel({
  shell,
  setDetachedSlot,
  floatingDrag,
  dockResize,
  style,
  onEscape,
}: {
  shell: 'dock' | 'floating'
  setDetachedSlot: (node: HTMLDivElement | null) => void
  floatingDrag: ReturnType<typeof useFloatingPanelPosition> | null
  dockResize: ReturnType<typeof useDockResizeHandle> | null
  style: React.CSSProperties
  onEscape: () => void
}) {
  const pipActive = useAidaPipStore(state => state.active)
  const drop = useAidaPanelImageDrop()
  const fileOver = useAidaFileDropStore(state => state.over)
  return (
    <aside
      ref={drop.setDropTarget}
      role="dialog"
      aria-label="Aida"
      style={style}
      onKeyDown={event => {
        event.stopPropagation()
        if (event.key === 'Escape' && !event.defaultPrevented) {
          if (useAidaMediaLightboxStore.getState().item) {
            closeAidaMediaLightbox({ suppressAidaClose: true })
            return
          }
          onEscape()
        }
      }}
      data-aida-shell={shell}
      className={tw(
        'fixed isolate flex flex-col overflow-hidden border border-theme_semantic_surface_border_color-subtle bg-theme_semantic_surface_primary-background-elevated text-theme_semantic_surface_ui_foreground-color-strong shadow-3-smooth outline-none',
        shell === 'floating' ? 'rounded-4' : 'rounded-none',
      )}
    >
      {pipActive ? <AidaAwayChrome floatingDrag={floatingDrag} /> : null}
      <div ref={setDetachedSlot} className="isolate min-h-0 flex-1 overflow-hidden">
        {pipActive ? <AidaPipAwayNotice /> : null}
      </div>
      {dockResize ? <DockResizeHandle resize={dockResize} /> : null}
      {drop.showDrop || fileOver ? (
        <AidaImageDropOverlay over={drop.isOver || fileOver} kind={fileOver ? 'file' : 'image'} />
      ) : null}
    </aside>
  )
}
